import { h, useEffect, useMemo, useRef } from "@lukekaalim/act"
import { Diff } from "./diff";
import { Animation, getAnimationPoint, lerp } from "./curve";
import { useRefresh } from "./refresher";

export type ListStateHandler<TElement, TState, TKey> = {
  createInitialState(elements: ReadonlyArray<TElement>): TState[];

  getElementKey(element: TElement): TKey;
  getStateKey(state: TState): TKey,

  isKeyEqual(left: TKey, right: TKey): boolean,

  insert(key: TKey, index: number, element: TElement) : TState;
  update(key: TKey, index: number, element: TElement, state: TState): TState;
  remove(key: TKey, state: TState): TState;

  isStillExiting(key: TKey, state: TState): boolean
}

/**
 * Build a system for creating objects
 * based on various events and state variables around a list
 * @returns 
 */
export const createListStateHook = <TElement, TState, TKey>(handler: ListStateHandler<TElement, TState, TKey>) => {
  return (list: ReadonlyArray<TElement>) => {
    const persisting = useRef<Map<TKey, TState>>(new Map()).get();
    const keysRef = useRef<TKey[]>([]);
    const exitingRef = useRef<TState[]>([])

    const initializedRef = useRef(false);

    useMemo(() => {
      if (!initializedRef.get()) {
        const initial = handler.createInitialState(list);
        for (const entry of initial) {
          const key = handler.getStateKey(entry)
          keysRef.get().push(key)
          persisting.set(key, entry);
        }
        initializedRef.set(true);
        return;
      }
      const keys = keysRef.get();
      const exiting = exitingRef.get();

      const nextKeys = list.map(el => handler.getElementKey(el));
      const nextExiting = [...exiting.filter(state => handler.isStillExiting(handler.getStateKey(state), state))];

      const diff = Diff.generate(keys, nextKeys, handler.isKeyEqual);
      for (const removed of diff.removed) {
        const key = keys[removed];

        let state: TState = persisting.get(key)!;
        state = handler.remove(key, state)
        persisting.delete(key);
        nextExiting.push(state);
      }
      for (let i = 0; i < diff.transform.length; i++) {
        const prevIndex = diff.transform[i];
        const key = keys[prevIndex];
        const element = list[i];
        
        let state = persisting.get(key);
        if (!state) {
          state = handler.insert(handler.getElementKey(element), i, element)
        } else {
          state = handler.update(key, i, element, state);
        }
        persisting.set(handler.getStateKey(state), state);
      }
      keysRef.set(nextKeys);
      exitingRef.set(nextExiting);
    }, [list])

    return useMemo(() => [
      ...keysRef.get().map(key => persisting.get(key)!),
      ...exitingRef.get(),
    ], [list])
  }
}

export function easeOutQuad(x: number): number {
  return 1 - (1 - x) * (1 - x);
}
export function easeOutExpo(x: number): number {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
}

export const createListAnimationHook = <TElement>(
  getKey: (element: TElement) => string,
  duration: number = 400,
  createInitial = () => Animation(0, 0, [1, 1], ([a, b], t) => lerp(a, b, easeOutQuad(t)))
) => {
  type State = {
    key: string,
    state: 'entering' | 'exiting',
    anim: Animation<number>,
    element: TElement,
    index: number,
    position: Animation<number>
  }

  const hook = createListStateHook<TElement, State, string>({
    isKeyEqual(left, right) {
      return left === right;
    },
    isStillExiting(key, state) {
      const now = performance.now();
      const point = getAnimationPoint(state.anim, now);
      return point < 2;
    },
    getElementKey(element) {
      return getKey(element)
    },
    getStateKey(state) {
      return state.key;
    },

    createInitialState(elements) {
      return elements.map((element, index) => {
        return {
          element,
          key: getKey(element),
          state: 'entering',
          anim: createInitial(),
          index,
          position: Animation(0, 0, [index, index], ([a, b], t) => lerp(a, b, easeOutQuad(t))),
        }
      })
    },
    insert(key, index, element) {
      const now = performance.now();
      return {
        key,
        element,
        state: 'entering',
        anim: Animation(now, duration, [0, 1], ([a, b], t) => lerp(a, b, easeOutQuad(t))),
        index,
        position: Animation(0, 0, [index, index], ([a, b], t) => lerp(a, b, easeOutQuad(t))),
      }
    },
    update(key, index, element, state) {
      state.element = element;
      if (state.index !== index) {
        const now = performance.now();
        const currentPosition = getAnimationPoint(state.position, now);
        state.position = Animation(now, duration, [currentPosition, index], state.position.interpolate);
        state.index = index;
      }
      return state;
    },
    remove(key, state) {
      const now = performance.now();
      const point = getAnimationPoint(state.anim, now);
      return {
        key,
        element: state.element,
        state: 'exiting',
        index: state.index,
        position: state.position,
        anim: Animation(now, duration, [point, 2], ([a, b], t) => lerp(a, b, easeOutQuad(t)))
      }
    },
  })

  return (list: readonly TElement[]) => {
    const [refresh] = useRefresh();
    const states = hook(list)

    useEffect(() => {
      if (states.filter(s => s.state === 'exiting').length < 1)
        return;

      const id = setTimeout(() => {
        refresh();
      }, duration);

      return () => clearTimeout(id);
    }, [...states.filter(s => s.state === 'exiting').map(s => s.key)])

    return states;

  }
}